import React, { createContext, useContext, useState, useEffect } from 'react';

// Define available currencies with their symbols and conversion rates (relative to USD)
export const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', rate: 3800 },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', rate: 130 },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', rate: 2600 },
  { code: 'RWF', symbol: 'RWF', name: 'Rwandan Franc', rate: 1250 }
];

interface CurrencyContextType {
  currency: {
    code: string;
    symbol: string;
    name: string;
    rate: number;
  };
  setCurrency: (currencyCode: string) => void;
  formatPrice: (price: number) => string;
  convertPrice: (price: number) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    // Try to get saved currency from localStorage
    const savedCurrency = localStorage.getItem('preferredCurrency');
    // Find the currency in our list or default to USD
    return currencies.find(c => c.code === savedCurrency) || currencies[0];
  });

  // Update localStorage when currency changes
  useEffect(() => {
    localStorage.setItem('preferredCurrency', currency.code);
  }, [currency]);

  const setCurrency = (currencyCode: string) => {
    const newCurrency = currencies.find(c => c.code === currencyCode);
    if (newCurrency) {
      setCurrencyState(newCurrency);
    }
  };

  // Convert price from USD to selected currency
  const convertPrice = (priceInUSD: number): number => {
    return priceInUSD * currency.rate;
  };

  // Format price with currency symbol and appropriate decimal places
  const formatPrice = (priceInUSD: number): string => {
    const convertedPrice = convertPrice(priceInUSD);
    
    // Format based on currency
    if (currency.code === 'UGX' || currency.code === 'KES' || currency.code === 'TZS' || currency.code === 'RWF') {
      // For currencies with large values, show no decimal places
      return `${currency.symbol} ${Math.round(convertedPrice).toLocaleString()}`;
    } else {
      // For major currencies, show 2 decimal places
      return `${currency.symbol} ${convertedPrice.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, convertPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};